// ============================================================================
// AppointmentRescheduleDialog — перенос записи на другое свободное окно.
// Специалист и источник НЕ меняются (перенос = тот же врач, другое время);
// занятость пересчитывается по серверным записям, переносимая запись исключена.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { friendlyError } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { AppointmentSlotPicker } from '@/components/medic/appointments/AppointmentSlotPicker';
import { useSlotAvailability } from '@/components/medic/appointments/useSlotAvailability';
import {
  CORE_DEFAULT_DURATION,
  localDateKey,
  localSlotLabel,
  valueDateKey,
  valueDateTimeLabel,
  type AppointmentCapabilities,
  type AppointmentItem,
  type AppointmentPerson,
  type AppointmentScheduleSettings,
  type AppointmentSlot,
} from '@/lib/medicAppointments';

export interface AppointmentRescheduleDialogProps {
  item: AppointmentItem | null;
  capabilities: AppointmentCapabilities;
  settings: AppointmentScheduleSettings;
  onClose: () => void;
  onOpenSettings: () => void;
  /** Настройка сетки — только врачу; пациент переносит в рамках общих окон. */
  allowSettings?: boolean;
  onSubmit: (item: AppointmentItem, startAt: string) => Promise<void>;
}

export function AppointmentRescheduleDialog({
  item, capabilities, settings, onClose, onOpenSettings, allowSettings = true, onSubmit,
}: AppointmentRescheduleDialogProps) {
  const [dateKey, setDateKey] = useState('');
  const [durationMin, setDurationMin] = useState(0);
  const [selectedStart, setSelectedStart] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!item) return;
    setDateKey(valueDateKey(item.startAt) || localDateKey(new Date()));
    setDurationMin(item.source === 'core' ? CORE_DEFAULT_DURATION : (item.durationMin || settings.durations[item.source]));
    setSelectedStart('');
    setError('');
  }, [item]);

  const availability = useSlotAvailability({
    capabilities,
    source: item?.source || 'core',
    doctorId: item?.doctorId || '',
    dateKey,
    durationMin,
    settings,
    excludeKey: item?.key,
    enabled: !!item,
  });

  const specialists = useMemo<AppointmentPerson[]>(() => (item ? [{
    id: item.doctorId,
    name: item.doctorName || 'Специалист',
    source: item.source,
    sources: [item.source],
  }] : []), [item]);

  const scopedCapabilities = useMemo<AppointmentCapabilities>(() => ({
    core: item?.source === 'core' ? capabilities.core : { ...capabilities.core, available: false },
    psy: item?.source === 'psy' ? capabilities.psy : { ...capabilities.psy, available: false },
  }), [item?.source, capabilities]);

  if (!item) return null;

  const canSubmit = !!selectedStart && !busy
    && availability.slots.some((slot) => slot.startAt === selectedStart && slot.available);

  const selectSlot = (slot: AppointmentSlot) => {
    setSelectedStart(slot.startAt);
    setError('');
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      await onSubmit(item, selectedStart);
      onClose();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <UniversalWindow
      open={!!item}
      onClose={onClose}
      title="Перенос записи"
      width="w-[min(760px,96vw)]"
      height="h-[min(82vh,90vh)]"
      topBar={(
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-7 px-2" onClick={onClose} disabled={busy}>Отмена</Button>
          <Button size="sm" className="h-7 px-2" onClick={() => void submit()} disabled={!canSubmit || busy}>
            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            Перенести
          </Button>
        </div>
      )}
    >
      <div className="h-full space-y-3 overflow-auto p-4">
        <div className="rounded-lg border bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
          Текущая запись: <span className="text-foreground">{valueDateTimeLabel(item.startAt)}</span>
          {' · '}{item.doctorName || 'Специалист'}. Выберите новое время ниже.
        </div>
        <AppointmentSlotPicker
          capabilities={scopedCapabilities}
          specialists={specialists}
          source={item.source}
          onSourceChange={() => undefined}
          specialistId={item.doctorId}
          onSpecialistChange={() => undefined}
          dateKey={dateKey}
          onDateChange={(key) => { setDateKey(key); setSelectedStart(''); }}
          durationMin={durationMin}
          onDurationChange={(minutes) => { setDurationMin(minutes); setSelectedStart(''); }}
          slots={availability.slots}
          closed={availability.closed}
          loading={availability.loading}
          error={availability.error}
          selectedStart={selectedStart}
          onSelectSlot={selectSlot}
          onOpenSettings={onOpenSettings}
          onRefresh={availability.reload}
          patientMode
          showSettings={allowSettings}
        />
        {selectedStart && !availability.slots.some((slot) => slot.startAt === selectedStart && slot.available) && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            Выбранное окно больше недоступно. Выберите другое время.
          </div>
        )}
        {selectedStart && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
            Новое время: <span className="font-medium">{localSlotLabel(selectedStart)}</span>
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}
      </div>
    </UniversalWindow>
  );
}
